// 🔍 기기 검수 탭 - 고급 테이블 기반 인터페이스
import React, { useState, useCallback } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  Stack,
  Typography,
  Tooltip,
  Avatar,
  LinearProgress,
  Menu,
  MenuItem,
  Divider,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Grid,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  TrendingUp as TrendingUpIcon,
  DevicesOther as DeviceIcon,
  MonetizationOn as MoneyIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  PlaylistAddCheck as BulkIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

import type { Application, DeviceInfo } from '../../types/application';
import { getDeviceStatusColor } from '../../../../constants/statusColors';
import { DEVICE_STATUS_LIST } from '../../../../constants/statusList';
import { useDeviceManagement } from '../orderDetail/hooks/useDeviceManagement';
import { useAlertManagement } from '../orderDetail/hooks/useAlertManagement';

// 🎨 Dialog 컴포넌트들
import DeviceEditDialog from './dialogs/DeviceEditDialog';
import BulkActionDialog from './dialogs/BulkActionDialog';

// 🎯 Props 인터페이스
interface DeviceInspectionTabProps {
  application: Application;
  onUpdate: (updatedApplication: Application) => void;
}

// 📱 기기 카드 컴포넌트 (모바일용)  
interface DeviceCardProps {
  device: DeviceInfo;
  index: number;
  onEdit: (device: DeviceInfo) => void;
  onStatusChange: (device: DeviceInfo, newStatus: string) => void;
  loading?: boolean;
}

const DeviceCard: React.FC<DeviceCardProps> = ({ device, index, onEdit, onStatusChange, loading }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleStatusMenuClick = (status: string) => {
    onStatusChange(device, status);
    handleMenuClose();
  };

  const formatPrice = (price: number | null) => {
    if (price === null || price === undefined) return '-';
    return `₩${price.toLocaleString()}`;
  };

  return (
    <Card
      elevation={0}
      onClick={() => onEdit(device)}
      sx={{
        borderRadius: 3,
        border: '1px solid #e1e8ed',
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        background: 'linear-gradient(145deg, #ffffff 0%, #f8fafd 100%)',
        cursor: 'pointer',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
          borderColor: '#1976d2',
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {/* 🏷️ 헤더 - 기기 정보 + 액션 */}
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ flex: 1 }}>
            <Avatar
              sx={{
                bgcolor: 'primary.main',
                width: 40,
                height: 40,
                fontSize: '1.1rem',
                fontWeight: 700,
              }}
            >
              {index + 1}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {(() => {
                // 태블릿/스마트폰 파싱 로직
                const deviceParts = device.deviceModel.split(' - ');
                let modelName = '';
                let storage = '';
                let connectivity = '';
                let userGrade = '';
                
                if (device.deviceType === '태블릿' || device.deviceType === '스마트폰') {
                  if (deviceParts.length >= 3) {
                    const lastPart = deviceParts[deviceParts.length - 1];
                    if (/^(Wi-Fi|Wi-Fi \+ Cellular)$/i.test(lastPart)) {
                      connectivity = lastPart;
                      const remainingParts = deviceParts.slice(0, -1);
                      const storagePattern = /(\d+(?:\.\d+)?\s*(GB|TB))/i;
                      for (let j = remainingParts.length - 1; j >= 0; j--) {
                        if (storagePattern.test(remainingParts[j])) {
                          storage = remainingParts[j];
                          break;
                        }
                      }
                      modelName = remainingParts[0] || device.deviceModel;
                    } else {
                      modelName = deviceParts[0];
                      if (/(\d+(?:\.\d+)?\s*(GB|TB))/i.test(deviceParts[1])) {
                        storage = deviceParts[1];
                      }
                    }
                  } else {
                    modelName = deviceParts[0] || device.deviceModel;
                  }
                if (device.userSelectedGrade) {
                  userGrade = String(device.userSelectedGrade);
                }
                } else {
                  modelName = device.deviceModel;
                }
                
                return (
                  <>
                    <Typography 
                      variant="subtitle1" 
                      fontWeight={700} 
                      sx={{ 
                        color: 'text.primary',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {modelName} {storage}
                      {/* 신청 시 등급 표시 */}
                      {userGrade && (
                        <Chip
                          label={`신청시 ${userGrade}급`}
                          size="small"
                          sx={{
                            ml: 1,
                            bgcolor: 'rgba(99, 102, 241, 0.1)',
                            color: '#6366f1',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            height: 20,
                          }}
                        />
                      )}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        {device.deviceType}
                      </Typography>
                      {/* 연결 방식 표시 */}
                      {connectivity && (
                        <Chip
                          label={connectivity}
                          size="small"
                          sx={{
                            bgcolor: 'rgba(59, 130, 246, 0.1)',
                            color: '#3b82f6',
                            fontWeight: 600,
                            fontSize: '0.65rem',
                            height: 18,
                          }}
                        />
                      )}
                      {/* 신청 시 예상 금액 표시 */}
                      {device.estimatedPrice !== undefined && (
                        <Chip
                          label={`신청시 예상: ${device.estimatedPrice && device.estimatedPrice > 0 ? `₩${device.estimatedPrice.toLocaleString()}` : '가격 미설정'}`}
                          size="small"
                          sx={{
                            bgcolor: device.estimatedPrice && device.estimatedPrice > 0 ? 'rgba(5, 150, 105, 0.1)' : 'rgba(107, 114, 128, 0.1)',
                            color: device.estimatedPrice && device.estimatedPrice > 0 ? '#059669' : '#6b7280',
                            fontWeight: 600,
                            fontSize: '0.65rem',
                            height: 18,
                          }}
                        />
                      )}
                    </Box>
                  </>
                );
              })()}
            </Box>
          </Stack>

          <Stack direction="row" spacing={1}>
            <Tooltip title="편집">
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(device);
                }}
                disabled={loading}
                sx={{
                  bgcolor: 'primary.50',
                  color: 'primary.main',
                  '&:hover': {
                    bgcolor: 'primary.100',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="상태 변경">
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleMenuClick(e);
                }}
                disabled={loading}
                sx={{
                  bgcolor: 'grey.50',
                  color: 'grey.700',
                  '&:hover': {
                    bgcolor: 'grey.100',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {/* 📊 상태 및 등급 */}
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <Chip
            label={device.status}
            sx={{
              bgcolor: getDeviceStatusColor(device.status),
              color: 'white',
              fontWeight: 600,
              fontSize: '0.75rem',
              height: 28,
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}
          />
          {device.grade && (
            <Chip
              label={device.grade}
              variant="outlined"
              sx={{
                borderColor: 'primary.main',
                color: 'primary.main',
                fontSize: '0.75rem',
                height: 28,
                borderRadius: 2,
                fontWeight: 600,
              }}
            />
          )}
        </Stack>

        {/* 💰 가격 정보 - 신청시 vs 검수시 비교 */}
        <Grid container spacing={2}>
          {/* 신청 시 예상 금액 */}
          {device.estimatedPrice !== undefined && (
            <Grid item xs={4}>
              <Box 
                sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  bgcolor: device.estimatedPrice && device.estimatedPrice > 0 ? 'rgba(99, 102, 241, 0.05)' : 'rgba(107, 114, 128, 0.05)',
                  border: device.estimatedPrice && device.estimatedPrice > 0 ? '1px solid rgba(99, 102, 241, 0.2)' : '1px solid rgba(107, 114, 128, 0.2)',
                }}
              >
                <Typography variant="caption" fontWeight={600} sx={{ 
                  fontSize: '0.65rem',
                  color: device.estimatedPrice && device.estimatedPrice > 0 ? '#6366f1' : '#6b7280'
                }}>
                  신청시 예상
                </Typography>
                <Typography variant="body2" fontWeight={700} sx={{ 
                  fontSize: '0.9rem',
                  color: device.estimatedPrice && device.estimatedPrice > 0 ? '#6366f1' : '#6b7280'
                }}>
                  {device.estimatedPrice && device.estimatedPrice > 0 ? `₩${device.estimatedPrice.toLocaleString()}` : '가격 미설정'}
                </Typography>
              </Box>
            </Grid>
          )}
          <Grid item xs={device.estimatedPrice !== undefined ? 4 : 6}>
            <Box 
              sx={{ 
                p: 1.5, 
                borderRadius: 2, 
                bgcolor: 'primary.50',
                border: '1px solid',
                borderColor: 'primary.100',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                <MoneyIcon sx={{ color: 'primary.main', fontSize: 14 }} />
                <Typography variant="caption" color="primary.main" fontWeight={600} sx={{ fontSize: '0.65rem' }}>
                  검수 가격
                </Typography>
              </Stack>
              <Typography variant="body2" fontWeight={700} color="primary.main" sx={{ fontSize: '0.9rem' }}>
                {formatPrice(device.price_offer)}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={device.estimatedPrice !== undefined ? 4 : 6}>
            <Box 
              sx={{ 
                p: 1.5, 
                borderRadius: 2, 
                bgcolor: 'success.50',
                border: '1px solid',
                borderColor: 'success.100',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                <TrendingUpIcon sx={{ color: 'success.main', fontSize: 14 }} />
                <Typography variant="caption" color="success.main" fontWeight={600} sx={{ fontSize: '0.65rem' }}>
                  최종 가격
                </Typography>
              </Stack>
              <Typography variant="body2" fontWeight={700} color="success.main" sx={{ fontSize: '0.9rem' }}>
                {formatPrice(device.final_price)}
              </Typography>
            </Box>
          </Grid>
        </Grid>
        
        {/* 가격 변동 안내 */}
        {(device.deviceType === '태블릿' || device.deviceType === '스마트폰') && device.estimatedPrice !== undefined && (
          <Box sx={{ mt: 2, p: 1.5, bgcolor: 'rgba(245, 158, 11, 0.05)', borderRadius: 2, border: '1px solid rgba(245, 158, 11, 0.2)' }}>
            <Typography variant="caption" color="#d97706" fontWeight={600} sx={{ fontSize: '0.7rem' }}>
              💡 매입 시점과 검수 시점 사이 가격 변동이 있을 수 있습니다
            </Typography>
          </Box>
        )}

        {/* 💡 가격 차이 표시 */}
        {device.price_offer && device.final_price && (
          <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                가격 차이
              </Typography>
              <Typography 
                variant="caption" 
                fontWeight={700}
                sx={{ 
                  color: device.final_price >= device.price_offer 
                    ? 'success.main' 
                    : 'error.main' 
                }}
              >
                {device.final_price >= device.price_offer ? '+' : ''}
                ₩{(device.final_price - device.price_offer).toLocaleString()}
              </Typography>
            </Stack>
          </Box>
        )}

        {/* 📝 추가 정보 */}
        {(device.deduction_reason || device.reject_reason) && (
          <Alert 
            severity={device.status === '반송' ? 'error' : 'info'} 
            sx={{ mt: 2, borderRadius: 2 }}
          >
            <Typography variant="body2" fontWeight={600}>
              {device.status === '반송' ? '반송 사유' : '차감 사유'}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {device.reject_reason || device.deduction_reason}
            </Typography>
          </Alert>
        )}
      </CardContent>

      {/* 📋 상태 변경 메뉴 */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            border: '1px solid #e1e8ed',
            minWidth: 180,
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Typography 
          variant="subtitle2" 
          sx={{ px: 2, py: 1, color: 'text.secondary', fontWeight: 600 }}
        >
          상태 변경
        </Typography>
        <Divider />
        {DEVICE_STATUS_LIST.map((status) => (
          <MenuItem
            key={status}
            onClick={(e) => {
              e.stopPropagation();
              handleStatusMenuClick(status);
            }}
            disabled={device.status === status}
            sx={{
              py: 1.5,
              px: 2,
              '&:hover': {
                bgcolor: `${getDeviceStatusColor(status)}20`,
              },
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  bgcolor: getDeviceStatusColor(status),
                }}
              />
              <Typography variant="body2" fontWeight={device.status === status ? 700 : 500}>
                {status}
              </Typography>
              {device.status === status && (
                <CheckIcon sx={{ fontSize: 16, color: 'success.main', ml: 'auto' }} />
              )}
            </Stack>
          </MenuItem>
        ))}
      </Menu>
    </Card>
  );
};

// 🖥️ 개별 기기 행 컴포넌트 (데스크탑용)
interface DeviceRowProps {
  device: DeviceInfo;
  index: number;
  onEdit: (device: DeviceInfo) => void;
  onStatusChange: (device: DeviceInfo, newStatus: string) => void;
  loading?: boolean;
}

const DeviceRow: React.FC<DeviceRowProps> = ({ device, index, onEdit, onStatusChange, loading }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const statusColor = getDeviceStatusColor(device.status);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleStatusSelect = (status: string) => {
    onStatusChange(device, status);
    handleMenuClose();
  };

  // 🎨 상태별 아이콘
  const getStatusIcon = (status: string) => {
    switch (status) {
      case '검수중': return <ScheduleIcon sx={{ fontSize: 16 }} />;
      case '가격안내': return <MoneyIcon sx={{ fontSize: 16 }} />;
      case '입금대기': return <CheckIcon sx={{ fontSize: 16 }} />;
      case '반송': return <CancelIcon sx={{ fontSize: 16 }} />;
      case '완료': return <TrendingUpIcon sx={{ fontSize: 16 }} />;
      default: return <DeviceIcon sx={{ fontSize: 16 }} />;
    }
  };

  return (
    <TableRow
      hover
      onClick={() => onEdit(device)}
      sx={{
        '&:hover': {
          bgcolor: 'rgba(25, 118, 210, 0.04)',
          transform: 'translateY(-1px)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        },
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: 'pointer',
      }}
    >
      {/* 기기 정보 */}
      <TableCell>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar
            sx={{
              bgcolor: 'primary.main',
              width: 40,
              height: 40,
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            {index + 1}
          </Avatar>
          <Box>
            {(() => {
              // 태블릿/스마트폰 파싱 로직
              const deviceParts = device.deviceModel.split(' - ');
              let modelName = '';
              let storage = '';
              let connectivity = '';
              let userGrade = '';
              
              if (device.deviceType === '태블릿' || device.deviceType === '스마트폰') {
                if (deviceParts.length >= 3) {
                  const lastPart = deviceParts[deviceParts.length - 1];
                  if (/^(Wi-Fi|Wi-Fi \+ Cellular)$/i.test(lastPart)) {
                    connectivity = lastPart;
                    const remainingParts = deviceParts.slice(0, -1);
                    const storagePattern = /(\d+(?:\.\d+)?\s*(GB|TB))/i;
                    for (let j = remainingParts.length - 1; j >= 0; j--) {
                      if (storagePattern.test(remainingParts[j])) {
                        storage = remainingParts[j];
                        break;
                      }
                    }
                    modelName = remainingParts[0] || device.deviceModel;
                  } else {
                    modelName = deviceParts[0];
                    if (/(\d+(?:\.\d+)?\s*(GB|TB))/i.test(deviceParts[1])) {
                      storage = deviceParts[1];
                    }
                  }
                } else {
                  modelName = deviceParts[0] || device.deviceModel;
                }
                if (device.userSelectedGrade) {
                  userGrade = String(device.userSelectedGrade);
                }
              } else {
                modelName = device.deviceModel;
              }
              
              return (
                <>
                  <Typography variant="subtitle2" fontWeight={600}>
                    {modelName} {storage}
                    {/* 신청 시 등급 표시 */}
                    {userGrade && (
                      <Chip
                        label={`신청시 ${userGrade}급`}
                        size="small"
                        sx={{
                          ml: 1,
                          bgcolor: 'rgba(99, 102, 241, 0.1)',
                          color: '#6366f1',
                          fontWeight: 600,
                          fontSize: '0.65rem',
                          height: 18,
                        }}
                      />
                    )}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {device.deviceType}
                    </Typography>
                    {/* 연결 방식 표시 */}
                    {connectivity && (
                      <Chip
                        label={connectivity}
                        size="small"
                        sx={{
                          bgcolor: 'rgba(59, 130, 246, 0.1)',
                          color: '#3b82f6',
                          fontWeight: 600,
                          fontSize: '0.6rem',
                          height: 16,
                        }}
                      />
                    )}
                    {/* 신청 시 예상 금액 표시 */}
                    {device.estimatedPrice !== undefined && device.estimatedPrice !== null && (
                      <Chip
                        label={`예상: ${device.estimatedPrice > 0 ? `₩${device.estimatedPrice.toLocaleString()}` : '가격 미설정'}`}
                        size="small"
                        sx={{
                          bgcolor: device.estimatedPrice > 0 ? 'rgba(5, 150, 105, 0.1)' : 'rgba(107, 114, 128, 0.1)',
                          color: device.estimatedPrice > 0 ? '#059669' : '#6b7280',
                          fontWeight: 600,
                          fontSize: '0.6rem',
                          height: 16,
                        }}
                      />
                    )}
                  </Box>
                </>
              );
            })()}
          </Box>
        </Stack>
      </TableCell>

      {/* 검수 가격 */}
      <TableCell>
        <Typography 
          variant="body2" 
          fontWeight={600}
          sx={{ 
            color: device.price_offer ? '#ff9800' : 'text.secondary',
          }}
        >
          {device.price_offer ? `₩${device.price_offer.toLocaleString()}` : '미정'}
        </Typography>
      </TableCell>

      {/* 최종 가격 */}
      <TableCell>
        <Typography 
          variant="body2" 
          fontWeight={600}
          sx={{ 
            color: device.final_price ? '#4caf50' : 'text.secondary',
          }}
        >
          {device.final_price ? `₩${device.final_price.toLocaleString()}` : '미정'}
        </Typography>
      </TableCell>

      {/* 상태 */}
      <TableCell>
        <Chip
          icon={getStatusIcon(device.status)}
          label={device.status}
          size="small"
          sx={{
            bgcolor: statusColor,
            color: 'white',
            fontWeight: 600,
            fontSize: '0.75rem',
            '& .MuiChip-icon': { color: 'white' },
            boxShadow: `0 2px 8px ${statusColor}40`,
          }}
        />
      </TableCell>

      {/* 등급 */}
      <TableCell>
        {device.grade && (
          <Chip
            label={device.grade}
            size="small"
            variant="outlined"
            sx={{
              fontWeight: 500,
              fontSize: '0.75rem',
            }}
          />
        )}
      </TableCell>

      {/* 작업 버튼 */}
      <TableCell>
        <Stack direction="row" spacing={1}>
          <Tooltip title="가격 및 상태 편집">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(device);
              }}
              disabled={loading}
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                width: 32,
                height: 32,
                '&:hover': {
                  bgcolor: 'primary.dark',
                  transform: 'scale(1.1)',
                  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)',
                },
                '&:disabled': {
                  bgcolor: 'grey.300',
                  color: 'grey.500',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <EditIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>

          <Tooltip title="상태 변경">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleMenuClick(e);
              }}
              disabled={loading}
              sx={{
                bgcolor: 'grey.100',
                color: 'grey.700',
                width: 32,
                height: 32,
                '&:hover': {
                  bgcolor: 'grey.200',
                  transform: 'scale(1.1)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                },
                '&:disabled': {
                  bgcolor: 'grey.100',
                  color: 'grey.400',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <MoreVertIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Stack>

        {/* 상태 변경 메뉴 */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
              border: '1px solid #e1e8ed',
            },
          }}
        >
          {DEVICE_STATUS_LIST.map((status) => (
            <MenuItem
              key={status}
              onClick={(e) => {
                e.stopPropagation();
                handleStatusSelect(status);
              }}
              selected={device.status === status}
              sx={{
                py: 1.5,
                px: 2,
                minWidth: 150,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.5}>
                {getStatusIcon(status)}
                <Typography variant="body2" fontWeight={500}>
                  {status}
                </Typography>
              </Stack>
            </MenuItem>
          ))}
        </Menu>
      </TableCell>
    </TableRow>
  );
};

// 📊 요약 카드 컴포넌트
const SummaryCards: React.FC<{ devices: DeviceInfo[] }> = ({ devices }) => {
  const totalDevices = devices.length;
  // ✅ '완료' 또는 '반송' 상태를 모두 처리 완료로 간주
  const PROCESSED_STATUSES = ['완료', '반송'];
  const completedDevices = devices.filter(d => PROCESSED_STATUSES.includes(d.status)).length;
  const totalPriceOffer = devices.reduce((sum, d) => sum + (d.price_offer || 0), 0);
  const totalFinalPrice = devices.reduce((sum, d) => sum + (d.final_price || 0), 0);

  const progressValue = totalDevices > 0 ? (completedDevices / totalDevices) * 100 : 0;

  return (
    <Grid container spacing={3} sx={{ mb: 4 }}>
      <Grid item xs={12} sm={6} md={3}>
        <Card elevation={0} sx={{ border: '1px solid #e1e8ed', borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                <DeviceIcon />
              </Avatar>
              <Box>
                <Typography variant="h4" fontWeight={700} color="primary">
                  {totalDevices}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  총 기기 수
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <Card elevation={0} sx={{ border: '1px solid #e1e8ed', borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{ bgcolor: '#ff9800', width: 48, height: 48 }}>
                <MoneyIcon />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight={700} sx={{ color: '#ff9800' }}>
                  ₩{totalPriceOffer.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  총 검수금액
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <Card elevation={0} sx={{ border: '1px solid #e1e8ed', borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{ bgcolor: '#4caf50', width: 48, height: 48 }}>
                <TrendingUpIcon />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight={700} sx={{ color: '#4caf50' }}>
                  ₩{totalFinalPrice.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  총 최종금액
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <Card elevation={0} sx={{ border: '1px solid #e1e8ed', borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Stack spacing={2}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  처리 진행률
                </Typography>
                <Typography variant="h6" fontWeight={700} color="primary">
                  {progressValue.toFixed(0)}%
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={progressValue}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: 'grey.200',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                  },
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {completedDevices}개 처리완료 / {totalDevices}개 전체
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

// 🎯 메인 컴포넌트
const DeviceInspectionTab: React.FC<DeviceInspectionTabProps> = ({ application, onUpdate }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const devices = application.devices || [];

  // 🔧 상태 관리
  const [editingDevice, setEditingDevice] = useState<DeviceInfo | null>(null);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // 🎯 훅 사용
  const deviceHook = useDeviceManagement(
    application,
    (updatedApplication) => {
      // Partial<Application>을 Application으로 변환
      if (updatedApplication.id) {
        onUpdate({ ...application, ...updatedApplication } as Application);
      }
    },
    (error) => {
      setSnackbar({
        open: true,
        message: error,
        severity: 'error',
      });
    }
  );

  const alertHook = useAlertManagement(application);

  // 🔧 기기 편집 핸들러
  const handleEditDevice = useCallback((device: DeviceInfo) => {
    setEditingDevice(device);
  }, []);

  // 🔧 기기 저장 핸들러
  const handleSaveDevice = useCallback(async (deviceId: string, updates: Partial<DeviceInfo>) => {
    try {
      await deviceHook.handleDeviceUpdate(deviceId, updates);
      setSnackbar({
        open: true,
        message: '기기 정보가 성공적으로 수정되었습니다.',
        severity: 'success',
      });

    } catch (error) {
      console.error('Device save error:', error);
    }
  }, [deviceHook]);

  // 🔧 상태 변경 핸들러
  const handleStatusChange = useCallback(async (device: DeviceInfo, newStatus: string) => {
    try {
      await deviceHook.handleDeviceUpdate(device.id, { status: newStatus as DeviceInfo['status'] });
      setSnackbar({
        open: true,
        message: `${device.deviceModel}의 상태가 "${newStatus}"로 변경되었습니다.`,
        severity: 'success',
      });
    } catch (error) {
      console.error('Status change error:', error);
    }
  }, [deviceHook]);

  // 🔧 일괄 처리 핸들러 (배치 업데이트 사용)
  const handleBulkUpdate = useCallback(async (deviceIds: string[], updates: Partial<DeviceInfo>) => {
    try {
      console.log(`🚀 일괄 처리 시작: ${deviceIds.length}개 기기`);
      console.log(`🚀 업데이트 내용:`, updates);
      
      // 🎯 배치 업데이트용 데이터 변환
      const batchUpdates: Record<string, Partial<DeviceInfo>> = {};
      deviceIds.forEach(deviceId => {
        batchUpdates[deviceId] = { ...updates };
      });
      
      console.log(`🎯 배치 업데이트 데이터:`, batchUpdates);
      
      // 🚀 배치 업데이트 실행 (상태 충돌 방지)
      await deviceHook.handleBatchDeviceUpdate(batchUpdates);

      setSnackbar({
        open: true,
        message: `${deviceIds.length}개 기기 일괄 처리가 완료되었습니다.`,
        severity: 'success',
      });

      // 🔔 일괄 알림톡 발송
      if (updates.status === '반송') {
        setTimeout(() => {
          setSnackbar({
            open: true,
            message: `${deviceIds.length}명의 고객에게 반송 알림톡이 발송되었습니다.`,
            severity: 'info',
          });
        }, 1500);
      }
    } catch (error) {
      console.error('일괄 처리 오류:', error);
      setSnackbar({
        open: true,
        message: `일괄 처리 중 오류가 발생했습니다: ${error instanceof Error ? error.message : '알 수 없는 오류'}`,
        severity: 'error',
      });
    }
  }, [deviceHook]);

  // 🔧 스낵바 닫기
  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  if (devices.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <DeviceIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          등록된 기기가 없습니다
        </Typography>
        <Typography variant="body2" color="text.secondary">
          신청서에 기기 정보가 포함되어 있지 않습니다.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* 📊 요약 카드들 */}
      <SummaryCards devices={devices} />

      {/* 🎯 액션 버튼들 - 반응형 */}
      <Stack 
        direction={{ xs: 'column', sm: 'row' }} 
        spacing={{ xs: 2, sm: 2 }} 
        sx={{ mb: 3 }}
      >
        <Stack direction="row" spacing={2} sx={{ flex: 1 }}>
          <Button
            variant="contained"
            startIcon={<BulkIcon />}
            onClick={() => setBulkDialogOpen(true)}
            disabled={deviceHook.loading}
            sx={{
              borderRadius: 3,
              textTransform: 'none',
              fontWeight: 700,
              px: { xs: 3, sm: 2 },
              py: { xs: 1.5, sm: 1 },
              fontSize: { xs: '1rem', sm: '0.875rem' },
              minHeight: { xs: 48, sm: 36 },
              background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
              boxShadow: '0 4px 20px rgba(25, 118, 210, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
                boxShadow: '0 6px 28px rgba(25, 118, 210, 0.4)',
                transform: 'translateY(-1px)',
              },
              '&:disabled': {
                background: 'grey.300',
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            일괄 처리
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => window.location.reload()}
            sx={{
              borderRadius: 3,
              textTransform: 'none',
              fontWeight: 600,
              px: { xs: 3, sm: 2 },
              py: { xs: 1.5, sm: 1 },
              fontSize: { xs: '1rem', sm: '0.875rem' },
              minHeight: { xs: 48, sm: 36 },
              borderWidth: 2,
              borderColor: 'grey.300',
              '&:hover': { 
                borderWidth: 2,
                borderColor: 'primary.main',
                bgcolor: 'primary.50',
                transform: 'translateY(-1px)',
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            새로고침
          </Button>
        </Stack>

        {/* 📊 처리 진행률 표시 - 반응형 */}
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            flexDirection: { xs: 'row', sm: 'row' },
            justifyContent: { xs: 'space-between', sm: 'flex-end' },
            ml: { xs: 0, sm: 'auto' },
          }}
        >
          <Typography 
            variant="body2" 
            color="text.secondary"
            fontWeight={600}
            sx={{ fontSize: { xs: '0.875rem', sm: '0.75rem' } }}
          >
            처리 진행률
          </Typography>
          <Box sx={{ minWidth: { xs: 100, sm: 120 } }}>
            <LinearProgress
              variant="determinate"
              // ✅ '완료' 또는 '반송' 상태를 모두 처리 완료로 간주
              value={devices.length > 0 ? (devices.filter(d => ['완료', '반송'].includes(d.status)).length / devices.length) * 100 : 0}
              sx={{
                height: { xs: 10, sm: 8 },
                borderRadius: 5,
                bgcolor: 'grey.200',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 5,
                  background: 'linear-gradient(135deg, #4caf50 0%, #81c784 100%)',
                  boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)',
                },
              }}
            />
          </Box>
          <Typography 
            variant="body2" 
            fontWeight={700} 
            color="primary"
            sx={{ 
              fontSize: { xs: '1rem', sm: '0.75rem' },
              minWidth: { xs: 40, sm: 35 },
            }}
          >
            {/* ✅ '완료' 또는 '반송' 상태를 모두 처리 완료로 간주 */}
            {devices.length > 0 ? Math.round((devices.filter(d => ['완료', '반송'].includes(d.status)).length / devices.length) * 100) : 0}%
          </Typography>
        </Box>
      </Stack>

      {/* 📋 기기 목록 - 반응형 (데스크탑: 테이블, 모바일: 카드) */}
      {isMobile ? (
        // 📱 모바일: 카드 레이아웃
        <Stack spacing={2}>
          {devices.map((device, index) => (
            <DeviceCard
              key={device.id}
              device={device}
              index={index}
              onEdit={handleEditDevice}
              onStatusChange={handleStatusChange}
              loading={deviceHook.loading}
            />
          ))}
        </Stack>
      ) : (
        // 🖥️ 데스크탑: 테이블 레이아웃
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: '1px solid #e1e8ed',
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            background: 'linear-gradient(145deg, #ffffff 0%, #f8fafd 100%)',
          }}
        >
          <TableContainer>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow 
                  sx={{ 
                    background: 'linear-gradient(135deg, #f8fafd 0%, #e3f2fd 100%)',
                    '& .MuiTableCell-head': {
                      borderBottom: '2px solid #e1e8ed',
                    }
                  }}
                >
                  <TableCell sx={{ fontWeight: 700, color: 'text.primary', py: 2 }}>
                    기기 정보
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>
                    검수 가격
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>
                    최종 가격
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>
                    상태
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>
                    등급
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>
                    작업
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {devices.map((device, index) => (
                  <DeviceRow
                    key={device.id}
                    device={device}
                    index={index}
                    onEdit={handleEditDevice}
                    onStatusChange={handleStatusChange}
                    loading={deviceHook.loading}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* 🎨 편집 Dialog */}
      <DeviceEditDialog
        open={!!editingDevice}
        device={editingDevice}
        onClose={() => setEditingDevice(null)}
        onSave={handleSaveDevice}
        loading={deviceHook.loading}
      />

      {/* 🎯 일괄 처리 Dialog */}
      <BulkActionDialog
        open={bulkDialogOpen}
        application={application}
        devices={devices}
        onClose={() => setBulkDialogOpen(false)}
        onBulkUpdate={handleBulkUpdate}
        loading={deviceHook.loading}
      />

      {/* 📢 스낵바 알림 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DeviceInspectionTab;